import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

type ConversationState = "idle" | "listening" | "processing" | "playing";

interface VoiceConversationProps {
  tributeId: string;
  tributeName: string;
}

// UUID v4 polyfill — crypto.randomUUID() requires iOS Safari 15.4+
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Output gain applied to voice playback.
const PLAYBACK_GAIN = 8.0;

// VAD: average frequency level that counts as speech. Raised 15 -> 25 so
// moderate ambient/outdoor noise no longer registers as speech (higher =
// silence auto-send still fires in noise). The manual Done button is the
// reliable backstop when the environment is loud enough to defeat the VAD.
const SPEECH_THRESHOLD = 25;
// VAD: ms of silence after speech before auto-sending
const SILENCE_DURATION = 1500;
// VAD: check interval in ms
const VAD_INTERVAL = 100;

// Generate a short silent PCM WAV as a blob URL for iOS session flipping.
function createSilentWavBlobUrl(): string {
  const sampleRate = 8000;
  const durationSec = 0.5;
  const numSamples = Math.floor(durationSec * sampleRate);
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, numSamples * 2, true);

  return URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
}

const VoiceConversation = ({ tributeId, tributeName }: VoiceConversationProps) => {
  const [state, setState] = useState<ConversationState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [sessionId] = useState(() => generateUUID());

  // Recording
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // VAD
  const analyserRef = useRef<AnalyserNode | null>(null);
  const vadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasSpeechRef = useRef(false);
  const lastSpeechTimeRef = useRef(0);
  const mediaSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);

  // Auto-resume flag — set to true during normal conversation flow,
  // false when user manually stops
  const autoResumeRef = useRef(true);
  // Guard flag — when true, sendAudio is a no-op (user pressed stop)
  const stoppedRef = useRef(false);

  // Playback (Web Audio)
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const keepAliveRef = useRef<AudioBufferSourceNode | null>(null);
  const keepAliveGainRef = useRef<GainNode | null>(null);

  // iOS session flip
  const silentAudioRef = useRef<HTMLAudioElement | null>(null);
  const silentAudioUrlRef = useRef<string | null>(null);

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  const stopKeepAlive = useCallback(() => {
    if (keepAliveRef.current) {
      try { keepAliveRef.current.stop(); } catch { /* */ }
      try { keepAliveRef.current.disconnect(); } catch { /* */ }
      keepAliveRef.current = null;
    }
    if (keepAliveGainRef.current) {
      try { keepAliveGainRef.current.disconnect(); } catch { /* */ }
      keepAliveGainRef.current = null;
    }
  }, []);

  const stopVAD = useCallback(() => {
    if (vadTimerRef.current) {
      clearInterval(vadTimerRef.current);
      vadTimerRef.current = null;
    }
    if (mediaSourceNodeRef.current) {
      try { mediaSourceNodeRef.current.disconnect(); } catch { /* */ }
      mediaSourceNodeRef.current = null;
    }
    analyserRef.current = null;
    hasSpeechRef.current = false;
    lastSpeechTimeRef.current = 0;
  }, []);

  const stopMicStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const stopMediaRecorder = useCallback(() => {
    if (mediaRecorderRef.current) {
      // Detach handlers BEFORE stopping so the onstop callback doesn't
      // fire sendAudio after the user has pressed stop.
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      if (mediaRecorderRef.current.state !== "inactive") {
        try { mediaRecorderRef.current.stop(); } catch { /* */ }
      }
      mediaRecorderRef.current = null;
    }
  }, []);

  // -----------------------------------------------------------------------
  // Cleanup on unmount
  // -----------------------------------------------------------------------

  useEffect(() => {
    return () => {
      stopVAD();
      stopMicStream();
      if (sourceRef.current) {
        try { sourceRef.current.stop(); } catch { /* */ }
        sourceRef.current = null;
      }
      if (keepAliveRef.current) {
        try { keepAliveRef.current.stop(); } catch { /* */ }
        keepAliveRef.current = null;
      }
      if (keepAliveGainRef.current) {
        try { keepAliveGainRef.current.disconnect(); } catch { /* */ }
        keepAliveGainRef.current = null;
      }
      if (silentAudioRef.current) {
        silentAudioRef.current.pause();
        silentAudioRef.current.src = "";
        silentAudioRef.current = null;
      }
      if (silentAudioUrlRef.current) {
        URL.revokeObjectURL(silentAudioUrlRef.current);
        silentAudioUrlRef.current = null;
      }
      audioContextRef.current?.close().catch(() => {});
      audioContextRef.current = null;
    };
  }, [stopVAD, stopMicStream]);

  // -----------------------------------------------------------------------
  // iOS audio session helpers
  // -----------------------------------------------------------------------

  const flipToPlaybackSession = useCallback(() => {
    const audio = silentAudioRef.current;
    if (!audio) return;
    try { audio.currentTime = 0; } catch { /* */ }
    const p = audio.play();
    if (p && typeof p.then === "function") {
      p.catch(() => {});
    }
  }, []);

  const startAudioSession = useCallback(() => {
    if (!audioContextRef.current) {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      audioContextRef.current = new AudioCtx({ latencyHint: "playback" });
    }

    const ctx = audioContextRef.current;
    ctx.resume().catch(() => {});

    stopKeepAlive();

    const silentBuffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const silentSource = ctx.createBufferSource();
    silentSource.buffer = silentBuffer;
    silentSource.loop = true;

    const keepAliveGain = ctx.createGain();
    keepAliveGain.gain.value = 0;

    silentSource.connect(keepAliveGain);
    keepAliveGain.connect(ctx.destination);
    silentSource.start(0);

    keepAliveRef.current = silentSource;
    keepAliveGainRef.current = keepAliveGain;

    if (!silentAudioRef.current) {
      const url = createSilentWavBlobUrl();
      silentAudioUrlRef.current = url;
      const audio = new Audio();
      audio.src = url;
      audio.preload = "auto";
      audio.playsInline = true;
      silentAudioRef.current = audio;
      const p = audio.play();
      if (p && typeof p.then === "function") {
        p.catch(() => {});
      }
    }

    return ctx;
  }, [stopKeepAlive]);

  // -----------------------------------------------------------------------
  // Core: start listening with VAD
  // -----------------------------------------------------------------------

  const startListening = useCallback(async () => {
    setError(null);
    autoResumeRef.current = true;
    stoppedRef.current = false;

    // Start audio session inside user gesture (first call only matters)
    startAudioSession();

    try {
      // Reuse the existing mic stream across turns to preserve the permission
      // grant — re-acquiring via getUserMedia() re-prompts on Safari/iOS. Only
      // request the mic on the FIRST acquisition (or if the stream is gone).
      let stream = streamRef.current;
      const tracksLive =
        !!stream && stream.getAudioTracks().some((t) => t.readyState === "live");
      if (!stream || !tracksLive) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
      }
      // Tracks are DISABLED (muted) between turns, not stopped. Re-enable BEFORE
      // building the recorder — a disabled track emits silence.
      stream.getAudioTracks().forEach((t) => (t.enabled = true));

      // Set up VAD via AnalyserNode
      const ctx = audioContextRef.current!;
      const sourceNode = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      sourceNode.connect(analyser);
      // Don't connect to destination — we only want to analyse, not play mic audio
      analyserRef.current = analyser;
      mediaSourceNodeRef.current = sourceNode;

      const frequencyData = new Uint8Array(analyser.frequencyBinCount);

      // Start MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });

      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        // Don't stop mic here — we may resume after playback.
        // Mic is stopped in stopMicStream() when needed.
        sendAudio(blob);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();

      // Reset VAD state
      hasSpeechRef.current = false;
      lastSpeechTimeRef.current = 0;

      // Start VAD polling
      vadTimerRef.current = setInterval(() => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(frequencyData);

        let sum = 0;
        for (let i = 0; i < frequencyData.length; i++) {
          sum += frequencyData[i];
        }
        const average = sum / frequencyData.length;

        if (average > SPEECH_THRESHOLD) {
          hasSpeechRef.current = true;
          lastSpeechTimeRef.current = Date.now();
        }

        // If the user has spoken and then been silent for SILENCE_DURATION, auto-send
        if (
          hasSpeechRef.current &&
          lastSpeechTimeRef.current > 0 &&
          Date.now() - lastSpeechTimeRef.current > SILENCE_DURATION
        ) {
          // Stop VAD and recording, send the audio
          stopVAD();
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop(); // triggers onstop → sendAudio
          }
          // MUTE the mic between turns instead of stopping it: track.stop()
          // releases the device and the next getUserMedia() re-prompts (esp.
          // Safari/iOS). Keep the grant + stream alive; disable the track so
          // nothing is captured during processing/playback — re-enabled in
          // startListening() next turn. stopMicStream() (track.stop()) now runs
          // ONLY on true session end (stopListening / stopPlayback / unmount).
          streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = false));
          setState("processing");
        }
      }, VAD_INTERVAL);

      setState("listening");
    } catch {
      setError("Microphone access required");
      setState("idle");
    }
  }, [startAudioSession, stopVAD]);

  // -----------------------------------------------------------------------
  // Manual stop — user taps button to end conversation
  // -----------------------------------------------------------------------

  const stopListening = useCallback(() => {
    autoResumeRef.current = false;
    stoppedRef.current = true;
    stopVAD();
    stopMediaRecorder();
    stopMicStream();
    chunksRef.current = [];
    setState("idle");
  }, [stopVAD, stopMediaRecorder, stopMicStream]);

  // Manual "Done" — finalize the CURRENT question and send it to be answered.
  // Mirrors the VAD auto-send branch in startListening(), but is triggered by a
  // tap and does NOT depend on hasSpeechRef — the whole point is to work when the
  // VAD fails to detect end-of-speech (e.g. loud/outdoor environments). Unlike
  // stopListening (cancel/abandon), this keeps onstop attached so onstop ->
  // sendAudio(blob) fires, and leaves stoppedRef/autoResumeRef untouched so
  // playback auto-resumes the next turn.
  const finishTurn = useCallback(() => {
    if (state !== "listening") return;
    stopVAD();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop(); // triggers onstop → sendAudio
    }
    // Mute the mic between turns (don't stop it — that would re-prompt on iOS).
    streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = false));
    setState("processing");
  }, [state, stopVAD]);

  const stopPlayback = useCallback(() => {
    autoResumeRef.current = false;
    stoppedRef.current = true;
    // Also clean up any lingering mic/VAD in case they're still active
    stopVAD();
    stopMediaRecorder();
    stopMicStream();
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch { /* */ }
      try { sourceRef.current.disconnect(); } catch { /* */ }
      sourceRef.current = null;
    }
    if (gainNodeRef.current) {
      try { gainNodeRef.current.disconnect(); } catch { /* */ }
      gainNodeRef.current = null;
    }
    stopKeepAlive();
    setState("idle");
  }, [stopKeepAlive, stopVAD, stopMediaRecorder, stopMicStream]);

  // -----------------------------------------------------------------------
  // Send audio and play response
  // -----------------------------------------------------------------------

  const sendAudio = async (audioBlob: Blob) => {
    // If the user pressed stop between the recorder stopping and this
    // callback firing, bail out — don't send or process anything.
    if (stoppedRef.current) return;

    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");
      formData.append("tribute_id", tributeId);
      formData.append("session_id", sessionId);

      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/voice-conversation`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        let errorMsg = `Server error (${response.status})`;
        try {
          const err = await response.json();
          errorMsg = err.error || errorMsg;
        } catch {
          // Response wasn't JSON — use status text
          errorMsg = `Server error: ${response.status} ${response.statusText}`;
        }
        console.error("[VoiceConversation] Edge function error:", errorMsg);
        throw new Error(errorMsg);
      }

      const arrayBuffer = await response.arrayBuffer();
      if (arrayBuffer.byteLength === 0) {
        throw new Error("Empty audio response from server");
      }

      const ctx = audioContextRef.current;
      if (!ctx) throw new Error("AudioContext not initialized");
      await ctx.resume();

      const audioBuffer = await new Promise<AudioBuffer>((resolve, reject) => {
        ctx.decodeAudioData(
          arrayBuffer.slice(0),
          (buf) => resolve(buf),
          (err) => reject(err || new Error("decodeAudioData failed"))
        );
      });

      // Clean up previous playback
      if (sourceRef.current) {
        try { sourceRef.current.stop(); } catch { /* */ }
        sourceRef.current = null;
      }
      if (gainNodeRef.current) {
        try { gainNodeRef.current.disconnect(); } catch { /* */ }
        gainNodeRef.current = null;
      }

      stopKeepAlive();
      flipToPlaybackSession();

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;

      const gainNode = ctx.createGain();
      gainNode.gain.value = PLAYBACK_GAIN;

      source.connect(gainNode);
      gainNode.connect(ctx.destination);

      source.onended = () => {
        if (sourceRef.current === source) {
          try { source.disconnect(); } catch { /* */ }
          try { gainNode.disconnect(); } catch { /* */ }
          sourceRef.current = null;
          gainNodeRef.current = null;
          flipToPlaybackSession();

          // Auto-resume listening if conversation wasn't manually stopped
          if (autoResumeRef.current) {
            startListening();
          } else {
            setState("idle");
          }
        }
      };

      sourceRef.current = source;
      gainNodeRef.current = gainNode;
      setState("playing");
      source.start(0);
    } catch (err: unknown) {
      console.error("[VoiceConversation] sendAudio error:", err);
      stopKeepAlive();
      const message = err instanceof Error ? err.message : "Something went wrong";
      setError(message);
      setState("idle");
    }
  };

  // -----------------------------------------------------------------------
  // Button handler
  // -----------------------------------------------------------------------

  const handleButtonClick = () => {
    if (state === "listening") {
      // Primary listening-state control is now "Done" — finalize the current
      // question and send it. Cancel/abandon is a secondary control below.
      finishTurn();
    } else if (state === "idle") {
      startListening();
    } else if (state === "playing") {
      stopPlayback();
    }
  };

  const isActive = state !== "idle";

  return (
    <div className="relative flex flex-col items-center gap-2">
      {/* Status panel — slides up when active */}
      <div
        className={`
          transition-all duration-200 ease-out origin-bottom-right
          ${isActive ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95 pointer-events-none"}
        `}
      >
        <div className="bg-card/95 backdrop-blur-md border border-border/50 rounded-2xl shadow-xl px-4 py-3 min-w-[160px]">
          {state === "listening" && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm text-foreground">Listening...</span>
              </div>
              <span className="text-xs text-muted-foreground">
                Tap Done when you've finished your question.
              </span>
              <button
                onClick={finishTurn}
                className="flex items-center justify-center gap-2 rounded-full bg-green-500 hover:bg-green-600 text-white font-medium text-sm px-4 py-2 shadow-md transition-colors active:scale-95"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6 9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Done
              </button>
              <button
                onClick={stopListening}
                className="text-xs text-muted-foreground hover:text-foreground underline self-start"
              >
                Cancel
              </button>
            </div>
          )}

          {state === "processing" && (
            <div className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="animate-spin text-muted-foreground">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" className="opacity-20" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              <span className="text-sm text-muted-foreground">Thinking...</span>
            </div>
          )}

          {state === "playing" && (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="flex items-end gap-[3px] h-4">
                  <span className="w-[3px] bg-primary rounded-full animate-[voice-bar_0.8s_ease-in-out_infinite]" style={{ height: "60%" }} />
                  <span className="w-[3px] bg-primary rounded-full animate-[voice-bar_0.8s_ease-in-out_0.2s_infinite]" style={{ height: "100%" }} />
                  <span className="w-[3px] bg-primary rounded-full animate-[voice-bar_0.8s_ease-in-out_0.4s_infinite]" style={{ height: "40%" }} />
                  <span className="w-[3px] bg-primary rounded-full animate-[voice-bar_0.8s_ease-in-out_0.1s_infinite]" style={{ height: "80%" }} />
                </div>
                <span className="text-sm text-foreground">Speaking</span>
              </div>
              <button
                onClick={stopPlayback}
                className="w-7 h-7 rounded-full bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center transition-colors active:scale-90"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className="text-red-500">
                  <rect x="0" y="0" width="10" height="10" rx="1.5" />
                </svg>
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Error display — outside the status panel so it stays visible
          even when state is idle and the panel fades out */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl px-4 py-3 max-w-xs text-center">
          <p className="text-sm text-destructive font-medium mb-1">Something went wrong</p>
          <p className="text-xs text-destructive/80 break-words">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-xs text-destructive/60 hover:text-destructive mt-2 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Mic button */}
      <button
        onClick={handleButtonClick}
        disabled={state === "processing"}
        className={`
          w-14 h-14 rounded-full flex items-center justify-center transition-all
          shadow-lg focus:outline-none active:scale-90
          ${state === "listening"
            ? "bg-green-500 shadow-green-500/25 animate-[gentle-pulse_1.5s_ease-in-out_infinite]"
            : state === "processing"
              ? "bg-muted cursor-not-allowed shadow-none"
              : state === "playing"
                ? "bg-primary/80 shadow-primary/20 animate-[gentle-pulse_2.5s_ease-in-out_infinite]"
                : "bg-primary hover:bg-primary/90 shadow-primary/20 hover:shadow-primary/30"
          }
        `}
        aria-label={
          state === "idle" ? `Talk to ${tributeName}` :
          state === "listening" ? "Done — finish your question" :
          state === "playing" ? "Stop" : "Processing"
        }
      >
        {state === "listening" ? (
          // Checkmark: tapping finalizes the question ("Done"). The green pulse +
          // "Listening..." panel still signal that recording is active.
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M20 6 9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : state === "processing" ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="animate-spin">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" className="opacity-20 text-foreground" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-foreground/60" />
          </svg>
        ) : state === "playing" ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        ) : (
          // Idle mic icon
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M12 1a4 4 0 0 0-4 4v6a4 4 0 0 0 8 0V5a4 4 0 0 0-4-4z" />
            <path d="M6 11a6 6 0 0 0 12 0" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <line x1="12" y1="17" x2="12" y2="21" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <line x1="8" y1="21" x2="16" y2="21" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
      </button>

      <span className="text-xl font-medium">
        {state === "listening" ? "Done" : "Have a Conversation"}
      </span>

      {/* Keyframe styles */}
      <style>{`
        @keyframes gentle-pulse {
          0%, 100% { box-shadow: 0 4px 14px rgba(0,0,0,0.15); transform: scale(1); }
          50% { box-shadow: 0 4px 20px rgba(0,0,0,0.25); transform: scale(1.05); }
        }
        @keyframes voice-bar {
          0%, 100% { transform: scaleY(0.4); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
};

export default VoiceConversation;
